import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { emailTemplatesAPI } from '../lib/firestore';
import { useToast } from '../components/ui/use-toast';
import type { EmailTemplate } from '../types';
import { Mail, Edit, Eye, Save, X, Plus } from 'lucide-react';

export const EmailTemplatesPage: React.FC = () => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form states
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [templateType, setTemplateType] = useState<EmailTemplate['type'] | ''>('');
  const [templateVariables, setTemplateVariables] = useState('');

  // Refs for textarea
  const subjectRef = React.useRef<HTMLInputElement>(null);
  const htmlBodyRef = React.useRef<HTMLTextAreaElement>(null);

  // Common variables by template type
  const getVariablesForType = (type: string): string[] => {
    const commonVars = {
      offboarding_notification: ['employeeName', 'employeeEmail', 'appList', 'link', 'deadline'],
      offboarding_reminder: ['employeeName', 'employeeEmail', 'appList', 'link', 'deadline', 'daysRemaining'],
      review_notification: ['reviewTitle', 'reviewDescription', 'appList', 'link', 'deadline'],
      review_reminder: ['reviewTitle', 'reviewDescription', 'appList', 'link', 'deadline', 'daysRemaining'],
    };
    return commonVars[type as keyof typeof commonVars] || [];
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await emailTemplatesAPI.getAll();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load email templates',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  const insertVariable = (variable: string, targetField: 'subject' | 'htmlBody') => {
    const varText = `{{${variable}}}`;

    if (targetField === 'subject' && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newValue = subject.substring(0, start) + varText + subject.substring(end);
      setSubject(newValue);

      // Set cursor position after inserted variable
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + varText.length, start + varText.length);
      }, 0);
    } else if (targetField === 'htmlBody' && htmlBodyRef.current) {
      const textarea = htmlBodyRef.current;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newValue = htmlBody.substring(0, start) + varText + htmlBody.substring(end);
      setHtmlBody(newValue);

      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + varText.length, start + varText.length);
      }, 0);
    }

    toast({
      title: 'Variable inserted',
      description: `${varText} has been inserted`,
      className: 'bg-indigo-50 border-indigo-200',
    });
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setSubject(template.subject);
    setHtmlBody(template.htmlBody);
    setIsEditDialogOpen(true);
  };

  const handlePreview = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsPreviewDialogOpen(true);
  };

  const handleCreateNew = () => {
    setSubject('');
    setHtmlBody('');
    setTemplateType('');
    setTemplateVariables('');
    setIsCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!templateType || !subject || !htmlBody) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        className: 'bg-red-50 border-red-200',
      });
      return;
    }

    try {
      setSaving(true);

      // Get variables for this template type
      const variables = getVariablesForType(templateType);

      await emailTemplatesAPI.create({
        type: templateType as EmailTemplate['type'],
        subject,
        htmlBody,
        variables,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      toast({
        title: 'Success',
        description: 'Email template created successfully',
        className: 'bg-green-50 border-green-200',
      });

      setIsCreateDialogOpen(false);
      await loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: 'Error',
        description: 'Failed to create email template',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    try {
      setSaving(true);

      await emailTemplatesAPI.update(editingTemplate.id, {
        subject,
        htmlBody,
        updatedAt: new Date().toISOString(),
      });

      toast({
        title: 'Success',
        description: 'Email template updated successfully',
        className: 'bg-green-50 border-green-200',
      });

      setIsEditDialogOpen(false);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: 'Failed to save email template',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSaving(false);
    }
  };

  const getTemplateTypeBadge = (type: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      offboarding_notification: { color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200', label: 'Offboarding' },
      offboarding_reminder: { color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200', label: 'Offboarding Reminder' },
      review_notification: { color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200', label: 'Review' },
      review_reminder: { color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200', label: 'Review Reminder' },
    };

    const badge = badges[type] || { color: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200', label: type };

    return (
      <Badge className={`${badge.color} hover:${badge.color}`}>
        {badge.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading templates...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Mail className="h-8 w-8" />
              Email Templates
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage email notification templates
            </p>
          </div>
          <Button onClick={handleCreateNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">{template.type}</CardTitle>
                    {getTemplateTypeBadge(template.type)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Subject</p>
                    <p className="text-sm font-semibold">{template.subject}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Available Variables</p>
                    <div className="flex flex-wrap gap-1">
                      {template.variables.map((variable) => (
                        <Badge
                          key={variable}
                          variant="outline"
                          className="text-xs font-mono"
                        >
                          {`{{${variable}}}`}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreview(template)}
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleEdit(template)}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {templates.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No email templates found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Email templates should be created via the seed data function
              </p>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Email Template</DialogTitle>
              <DialogDescription>
                Modify the email template. Use variables like {`{{variableName}}`} for dynamic content.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-4">
              {/* Left column - Form */}
              <div className="col-span-2 space-y-4">
                {editingTemplate && (
                  <div>
                    {getTemplateTypeBadge(editingTemplate.type)}
                  </div>
                )}

                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    ref={subjectRef}
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="htmlBody">HTML Body</Label>
                  <Textarea
                    ref={htmlBodyRef}
                    id="htmlBody"
                    value={htmlBody}
                    onChange={(e) => setHtmlBody(e.target.value)}
                    placeholder="Email HTML content..."
                    rows={15}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Right column - Variables */}
              <div className="col-span-1">
                <div className="sticky top-4">
                  <Label className="text-sm font-semibold mb-3 block">Available Variables</Label>
                  {editingTemplate && (
                    <div className="space-y-2 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800">
                      <p className="text-xs text-indigo-800 dark:text-indigo-200 mb-2">Click to insert into Subject:</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {editingTemplate.variables.map((variable) => (
                          <Badge
                            key={variable}
                            variant="outline"
                            className="text-xs font-mono cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
                            onClick={() => insertVariable(variable, 'subject')}
                          >
                            {`{{${variable}}}`}
                          </Badge>
                        ))}
                      </div>

                      <p className="text-xs text-indigo-800 dark:text-indigo-200 mb-2">Click to insert into Body:</p>
                      <div className="flex flex-wrap gap-1">
                        {editingTemplate.variables.map((variable) => (
                          <Badge
                            key={variable}
                            variant="outline"
                            className="text-xs font-mono cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
                            onClick={() => insertVariable(variable, 'htmlBody')}
                          >
                            {`{{${variable}}}`}
                          </Badge>
                        ))}
                      </div>

                      <div className="mt-3 pt-3 border-t border-indigo-300 dark:border-indigo-700">
                        <p className="text-xs text-indigo-700 dark:text-indigo-300 font-medium mb-1">Special Variables:</p>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">• <code>{'{{appList}}'}</code> - List of apps (HTML)</p>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">• <code>{'{{link}}'}</code> - Verification link</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Preview</DialogTitle>
              <DialogDescription>
                Preview of the email template with placeholder values
              </DialogDescription>
            </DialogHeader>

            {editingTemplate && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Subject:</p>
                  <p className="text-lg font-semibold">{editingTemplate.subject}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Body:</p>
                  <iframe
                    sandbox=""
                    srcDoc={editingTemplate.htmlBody}
                    className="border rounded-lg w-full h-96 bg-card"
                    title="Email preview"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => setIsPreviewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Template Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Email Template</DialogTitle>
              <DialogDescription>
                Create a new email template. Use variables like {`{{variableName}}`} for dynamic content.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-4">
              {/* Left column - Form */}
              <div className="col-span-2 space-y-4">
                <div>
                  <Label htmlFor="template-type">Template Type *</Label>
                  <Select
                    value={templateType}
                    onValueChange={(value) => setTemplateType(value as EmailTemplate['type'])}
                  >
                    <SelectTrigger id="template-type" className="mt-1">
                      <SelectValue placeholder="Select template type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="offboarding_notification">Offboarding Notification</SelectItem>
                      <SelectItem value="offboarding_reminder">Offboarding Reminder</SelectItem>
                      <SelectItem value="review_notification">Review Notification</SelectItem>
                      <SelectItem value="review_reminder">Review Reminder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="create-subject">Subject *</Label>
                  <Input
                    ref={subjectRef}
                    id="create-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="create-htmlBody">HTML Body *</Label>
                  <Textarea
                    ref={htmlBodyRef}
                    id="create-htmlBody"
                    value={htmlBody}
                    onChange={(e) => setHtmlBody(e.target.value)}
                    placeholder="Email HTML content..."
                    rows={15}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Right column - Variables */}
              <div className="col-span-1">
                <div className="sticky top-4">
                  <Label className="text-sm font-semibold mb-3 block">Available Variables</Label>
                  {templateType && (
                    <div className="space-y-2 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800">
                      <p className="text-xs text-indigo-800 dark:text-indigo-200 mb-2">Click to insert into Subject:</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {getVariablesForType(templateType).map((variable) => (
                          <Badge
                            key={variable}
                            variant="outline"
                            className="text-xs font-mono cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
                            onClick={() => insertVariable(variable, 'subject')}
                          >
                            {`{{${variable}}}`}
                          </Badge>
                        ))}
                      </div>

                      <p className="text-xs text-indigo-800 dark:text-indigo-200 mb-2">Click to insert into Body:</p>
                      <div className="flex flex-wrap gap-1">
                        {getVariablesForType(templateType).map((variable) => (
                          <Badge
                            key={variable}
                            variant="outline"
                            className="text-xs font-mono cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
                            onClick={() => insertVariable(variable, 'htmlBody')}
                          >
                            {`{{${variable}}}`}
                          </Badge>
                        ))}
                      </div>

                      <div className="mt-3 pt-3 border-t border-indigo-300 dark:border-indigo-700">
                        <p className="text-xs text-indigo-700 dark:text-indigo-300 font-medium mb-1">Special Variables:</p>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">• <code>{'{{appList}}'}</code> - List of apps (HTML)</p>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">• <code>{'{{link}}'}</code> - Verification link</p>
                      </div>
                    </div>
                  )}
                  {!templateType && (
                    <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                      Select a template type to see available variables
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={saving}>
                <Plus className="h-4 w-4 mr-2" />
                {saving ? 'Creating...' : 'Create Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
